import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { SpreadSheetService } from "../s25-spreadsheet/spreadsheet.service";
import { RowData } from "../s25-spreadsheet/types/sheet.js.types";
import { Conflicts } from "./import.service";

export interface ImportType {
    name: string;
    value: "events" | "locations" | "resources" | "organizations" | "contacts";
    completeText: string;
    shown: boolean;
    init: boolean;
    errors: string[];
    successMessage: string;
    conflicts?: Conflicts;
    onComplete?: (rows: RowData[]) => Promise<void>;
}

@TypeManagerDecorator("s25-ng-import")
@Component({
    selector: "s25-ng-import",
    template: `
        @if (init) {
            <label
                >Select Import Type&nbsp;
                <select
                    class="cn-form__control c-margin-bottom--single"
                    [(ngModel)]="selectedType"
                    (ngModelChange)="onSelectedTypeChange($event)"
                >
                    @for (type of importTypes; track type.value) {
                        <option [ngValue]="type">
                            {{ type.name }}
                        </option>
                    }
                </select>
            </label>

            @for (type of importTypes; track type.value) {
                @if (type.init) {
                    <div [hidden]="!type.shown">
                        @if (type.value === "events") {
                            <s25-ng-import-events [type]="type" [parentCd]="cd"></s25-ng-import-events>
                        }

                        @if (type.errors?.length) {
                            <div class="ngRed c-margin-bottom--single">Validation Errors</div>
                            <ul
                                [style.border-left]="'3px solid #dc3545'"
                                class="c-margin-left--single c-margin-bottom--single"
                            >
                                @for (error of type.errors; track $index) {
                                    <li class="c-margin-bottom--single">{{ error }}</li>
                                }
                            </ul>
                            @if (type.conflicts?.spConflicts?.length) {
                                <div class="c-margin-bottom--single">
                                    <span class="ngRed">Space Conflicts</span>
                                    <ul class="c-margin-left--single c-margin-bottom--single">
                                        @for (conflict of type.conflicts.spConflicts; track $index) {
                                            <li class="c-margin-bottom--single">
                                                <div>{{ conflict.conflictTypeName }} in {{ conflict.roomName }}</div>
                                                <div>
                                                    Candidate:
                                                    {{
                                                        conflict.candidateEventName
                                                            ? conflict.candidateEventName +
                                                              " (" +
                                                              conflict.candidateDateTimeStr +
                                                              ")"
                                                            : conflict.candidateDateTimeStr
                                                    }}
                                                </div>
                                                <div>
                                                    Conflict:
                                                    {{
                                                        conflict.conflictEventName
                                                            ? conflict.conflictEventName +
                                                              " (" +
                                                              conflict.conflictDateTimeStr +
                                                              ")"
                                                            : conflict.conflictDateTimeStr
                                                    }}
                                                </div>
                                            </li>
                                        }
                                    </ul>
                                </div>
                            }
                            @if (type.conflicts?.rsConflicts?.length) {
                                <div class="c-margin-bottom--single">
                                    <span class="ngRed">Resource Conflicts</span>
                                    <ul class="c-margin-left--single c-margin-bottom--single">
                                        @for (conflict of type.conflicts.rsConflicts; track $index) {
                                            <li class="c-margin-bottom--single">
                                                <div>
                                                    {{ conflict.conflictTypeName }} in {{ conflict.resourceName }}
                                                </div>
                                                <div>
                                                    Candidate: {{ conflict.candidateEventName }} ({{
                                                        conflict.candidateDateTimeStr
                                                    }})
                                                </div>
                                                <div>
                                                    Conflict:
                                                    {{
                                                        conflict.conflictEventName
                                                            ? conflict.conflictEventName +
                                                              "(" +
                                                              conflict.conflictDateTimeStr +
                                                              ")"
                                                            : conflict.conflictDateTimeStr
                                                    }}
                                                </div>
                                            </li>
                                        }
                                    </ul>
                                </div>
                            }
                        }

                        @if (type.successMessage) {
                            <div class="ngGreen c-margin-bottom--single">
                                {{ type.successMessage }}
                            </div>
                        }

                        <s25-ng-spreadsheet
                            class="c-margin-top--single"
                            [onComplete]="type.onComplete"
                            [completeText]="type.completeText"
                            [widthFunc]="widthFunc"
                        ></s25-ng-spreadsheet>
                    </div>
                }
            }
        }
    `,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportComponent implements OnInit {
    init = false;
    importTypes: ImportType[] = [
        {
            name: "Events",
            value: "events",
            completeText: "Import Events",
            shown: false,
            init: false,
            errors: [],
            conflicts: null,
            successMessage: "",
            onComplete: null, // defined in import.events.component.ts
        },
    ];
    selectedType: ImportType = null;
    widthFunc: () => number = () => Math.floor(window.innerWidth * 0.9);

    constructor(public cd: ChangeDetectorRef) {}

    onSelectedTypeChange(type: ImportType) {
        this.importTypes.forEach((t) => (t.shown = false));
        type.shown = true;
        type.init = true;
        this.cd.detectChanges();
    }

    async ngOnInit() {
        this.selectedType = this.importTypes[0];
        this.onSelectedTypeChange(this.selectedType);
        await SpreadSheetService.load();
        this.init = true;
        this.cd.detectChanges();
    }
}
